import type { ComponentProps } from "react"
import { useEffect, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useNavigate } from "@tanstack/react-router"
import { motion } from "motion/react"

import { cn } from "@productlane/lib/src/cn"
import { formatRelativeTime } from "@productlane/lib/src/date"
import { Button } from "@productlane/ui"
import { queries } from "@productlane/zero/queries"

import { FeedbackOriginIconRecord } from "@/lib/feedback"
import { useThreadContext } from "../../thread-context"
import { SidebarGroup, SidebarGroupToggle } from "./shared/sidebar-group"
import { useMeasuredHeight } from "./shared/use-measured-height"

type Props = ComponentProps<"div">

const STORAGE_KEY = "sidebar-section-open:recent-threads"

export function RecentThreads({ className, ...props }: Props) {
	const { thread } = useThreadContext()
	const [recentThreads] = useQuery(
		queries.recentThreads({
			companyId: thread.companyId ?? "",
			excludeThreadId: thread.id,
			limit: 3,
		}),
	)
	const navigate = useNavigate()

	const [isOpen, setIsOpen] = useState(true)
	useEffect(() => {
		try {
			const stored = window.localStorage.getItem(STORAGE_KEY)
			if (stored !== null) setIsOpen(stored === "1")
		} catch {
			// localStorage unavailable
		}
	}, [])
	const toggleOpen = () => {
		setIsOpen((prev) => {
			const next = !prev
			try {
				window.localStorage.setItem(STORAGE_KEY, next ? "1" : "0")
			} catch {
				// localStorage unavailable
			}
			return next
		})
	}

	const [contentHeight, setContentEl] = useMeasuredHeight()

	if (!thread.companyId) {
		return null
	}

	if (recentThreads.length === 0) {
		return null
	}

	return (
		<SidebarGroup className={cn("flex flex-col", className)} {...props}>
			<SidebarGroupToggle
				label="Recent"
				isOpen={isOpen}
				onToggle={toggleOpen}
			/>
			<motion.div
				initial={false}
				animate={{
					height: isOpen ? contentHeight : 0,
					opacity: isOpen ? 1 : 0,
				}}
				transition={{ duration: 0.22, ease: [0.23, 1, 0.32, 1] }}
				style={{ overflow: "hidden" }}
			>
				<div ref={setContentEl} className="space-y-0.5">
					{recentThreads.map((t) => {
						const OriginIcon = FeedbackOriginIconRecord[t.origin]
						return (
							<Button
								key={t.id}
								onClick={() => {
									void navigate({ to: "/threads/$id", params: { id: t.id } })
								}}
								className="flex w-full !max-w-none justify-between hover:bg-gray-150 dark:hover:bg-gray-800"
								variant="ghost"
								wrapperClassName="w-full flex flex-row items-center gap-2 text-start"
							>
								<OriginIcon
									strokeWidth={1.75}
									className="h-3 w-3 shrink-0 text-gray-500 dark:text-gray-400 [&:not(.lucide)]:fill-gray-500 [&:not(.lucide)]:dark:fill-gray-400"
								/>
								<div className="min-w-0 flex-1 select-none truncate text-xs tracking-tight text-tertiary">
									{t.title || "Untitled thread"}
								</div>
								<div className="shrink-0 select-none text-xs tabular-nums text-gray-400 dark:text-gray-500">
									{formatRelativeTime(new Date(t.createdAt))}
								</div>
							</Button>
						)
					})}
				</div>
			</motion.div>
		</SidebarGroup>
	)
}
